import type { CalendarDate } from "../shared/calendar-date";
import type { DailyDiarySummary } from "../diary/daily-summary";
import type { DailyNutrientTarget } from "../nutrition/nutrient-target";
import { convertNutrientAmount, normalizeNutrientUnit } from "../nutrition/nutrients";
import type { WeekPlan } from "./week-plan";
import { calculateDailyMacroTargets, type DailyMacroTargets } from "./macro-targets";

export interface NutrientTargetProgress {
  readonly nutrientKey: string;
  readonly displayName?: string;
  readonly targetAmount: number;
  readonly consumedAmount: number;
  readonly unit: DailyNutrientTarget["unit"];
}

export interface DailyPlanProgress {
  readonly date: CalendarDate;
  readonly allocatedCalories: number;
  readonly consumedCalories: number;
  readonly remainingCalories: number;
  readonly macroTargets: DailyMacroTargets;
  readonly proteinConsumedGrams: number;
  readonly proteinRemainingGrams: number;
  readonly nutrientTargets: readonly NutrientTargetProgress[];
}

export function calculateDailyPlanProgress(
  plan: WeekPlan,
  summary: DailyDiarySummary,
  nutrientTargets: readonly DailyNutrientTarget[],
): DailyPlanProgress {
  const allocation = plan.allocations.find((candidate) => candidate.date === summary.date);
  if (!allocation) throw new Error("Date does not belong to the week plan");
  const macroTargets = calculateDailyMacroTargets(
    allocation.calories,
    plan.fixedDailyProteinGrams,
    plan.macroDistribution,
    plan.dailyFloorPolicy.minimumFatGrams,
  );
  const nutrientProgress = nutrientTargets.map((target) => {
    const targetUnit = normalizeNutrientUnit(target.unit);
    const macroAmount =
      target.nutrientKey === "protein"
        ? summary.nutrients.macros.proteinGrams
        : target.nutrientKey === "carbohydrate"
          ? summary.nutrients.macros.carbohydrateGrams
          : target.nutrientKey === "fat"
            ? summary.nutrients.macros.fatGrams
            : target.nutrientKey === "fibre"
              ? (summary.nutrients.fibreGrams ?? 0)
              : undefined;
    const consumed = summary.nutrients.micronutrients[target.nutrientKey];
    const consumedAmount = macroAmount !== undefined
      ? convertNutrientAmount(macroAmount, "g", targetUnit)
      : consumed
        ? convertNutrientAmount(consumed.amount, consumed.unit, targetUnit)
        : 0;
    return {
      nutrientKey: target.nutrientKey,
      displayName: target.displayName,
      targetAmount: target.amount,
      consumedAmount,
      unit: targetUnit,
    };
  });
  return {
    date: summary.date,
    allocatedCalories: allocation.calories,
    consumedCalories: summary.calories,
    remainingCalories: allocation.calories - summary.calories,
    macroTargets,
    proteinConsumedGrams: summary.nutrients.macros.proteinGrams,
    proteinRemainingGrams: Math.max(0, plan.fixedDailyProteinGrams - summary.nutrients.macros.proteinGrams),
    nutrientTargets: nutrientProgress,
  };
}
